import React, { useMemo } from 'react';

import { IDelivery, IServerOrder } from '@rbi-ctg/menu';
import Currency from 'components/currency';
import { geographicalFeeLabelIdMap } from 'pages/account/account-orders/constants';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { getDeliveryFees } from 'utils/order';

import { Divider, Total, TotalLabel, TotalsRow } from './modal-order-details.styled';

const DeliveryPriceDetails = ({ delivery }: { delivery: IDelivery }) => {
  const {
    feeCents: deliveryFeeCents,
    isFreeDelivery,
    deliveryFeeDiscountCents,
    geographicalFeeCents,
    serviceFeeCents,
    smallCartFeeCents,
  } = getDeliveryFees({
    delivery,
  });

  const dropoffState = delivery?.dropoff?.state;

  // Use a custom label for geographical delivery fees as required
  const geographicalFeeLabelId =
    dropoffState && geographicalFeeLabelIdMap.hasOwnProperty(dropoffState)
      ? geographicalFeeLabelIdMap[dropoffState]
      : 'geographicalFee';

  return (
    <>
      {/* deliveryFee */}
      <TotalsRow>
        <TotalLabel focusable>{t('deliveryFee')}</TotalLabel>
        <Total focusable>
          <Currency amount={isFreeDelivery ? deliveryFeeDiscountCents : deliveryFeeCents} />
        </Total>
      </TotalsRow>

      {geographicalFeeCents > 0 && (
        <TotalsRow>
          <TotalLabel focusable>{t(geographicalFeeLabelId as TranslationKey)}</TotalLabel>
          <Total focusable>
            <Currency amount={geographicalFeeCents} />
          </Total>
        </TotalsRow>
      )}

      {serviceFeeCents > 0 && (
        <TotalsRow>
          <TotalLabel focusable>{t('serviceFee')}</TotalLabel>
          <Total focusable>
            <Currency amount={serviceFeeCents} />
          </Total>
        </TotalsRow>
      )}

      {smallCartFeeCents > 0 && (
        <TotalsRow>
          <TotalLabel focusable>{t('smallCartFee')}</TotalLabel>
          <Total focusable>
            <Currency amount={smallCartFeeCents} />
          </Total>
        </TotalsRow>
      )}

      <TotalsRow>
        <TotalLabel focusable>{t('tip')}</TotalLabel>
        <Total focusable>
          <Currency amount={delivery?.tipCents} />
        </Total>
      </TotalsRow>
    </>
  );
};

export const OrderPricesDetails = ({ serverOrder }: { serverOrder: IServerOrder }) => {
  const { cart, delivery } = serverOrder;
  const { subTotalCents, taxCents } = cart;

  const discountsAmount = useMemo(() => {
    return cart?.discounts?.reduce((acc, { value }) => acc + value, 0) || 0;
  }, [cart?.discounts]);

  const donationsAmount = useMemo(() => {
    return cart?.donations?.reduce((acc, { totalCents }) => acc + totalCents, 0) || 0;
  }, [cart?.donations]);

  return (
    <>
      <TotalsRow>
        <TotalLabel focusable>{t('subtotal')}</TotalLabel>
        <Total focusable>
          <Currency amount={subTotalCents} />
        </Total>
      </TotalsRow>
      {discountsAmount > 0 && (
        <TotalsRow>
          <TotalLabel focusable>{t('comboOfferSavings')}</TotalLabel>
          <Total focusable>
            <Currency amount={discountsAmount} />
          </Total>
        </TotalsRow>
      )}
      {donationsAmount > 0 && (
        <TotalsRow>
          <TotalLabel focusable>{t('donation')}</TotalLabel>
          <Total focusable>
            <Currency amount={donationsAmount} />
          </Total>
        </TotalsRow>
      )}
      {delivery && <DeliveryPriceDetails delivery={delivery} />}
      <TotalsRow>
        <TotalLabel focusable>{t('taxes')}</TotalLabel>
        <Total focusable>
          <Currency amount={taxCents} />
        </Total>
      </TotalsRow>

      <Divider />
    </>
  );
};
